import { motion } from "framer-motion";
import { Coffee, Croissant, Sandwich, Leaf, Briefcase, Heart, Users, Sunrise, Phone, ArrowRight, BoxIcon } from "lucide-react";
import { HamburgerMenu } from "../components/HamburgerMenu";
import { NewsletterSignup } from "../components/NewsletterSignup";
import { SEO } from "../components/SEO";
const BOOKING_URL = 'https://groundedmornings.com';
const whatWeBring = [{
  icon: Coffee,
  name: 'Mobile Espresso & Coffee Bar',
  description: 'A full espresso setup with our baristas behind it, pulling shots and steaming milk on site.'
}, {
  icon: BoxIcon,
  name: 'Batch Drip Coffee by the Box',
  description: 'Hot drip coffee by the gallon — the easy call for meetings and early starts. Decaf available.'
}, {
  icon: Leaf,
  name: 'Specialty Drinks',
  description: 'Matcha, chai, and cold brew for the folks who want something other than coffee.'
}, {
  icon: Croissant,
  name: 'Fresh Pastries',
  description: 'Croissants, muffins, and our Groundbreaker cookies, delivered fresh that morning.'
}, {
  icon: Sandwich,
  name: 'Made-to-Order Breakfast',
  description: 'Breakfast sandwiches and toasts built for your group, not pulled from a warmer.'
}];
const perfectFor = [{
  icon: Briefcase,
  name: 'Corporate Meetings & Offices',
  description: 'Client presentations, all-hands, team offsites, and the standing Monday morning.'
}, {
  icon: Heart,
  name: 'Weddings & Showers',
  description: 'A coffee bar for the reception, the morning-after brunch, or the getting-ready hours.'
}, {
  icon: Users,
  name: 'Community Events',
  description: 'School functions, fundraisers, markets, and neighborhood gatherings.'
}, {
  icon: Sunrise,
  name: 'Morning & Brunch Gatherings',
  description: 'Anything that starts early and runs better with something good in hand.'
}];
const steps = [{
  number: '01',
  name: 'Tell Us About Your Event',
  description: 'Share the date, the venue, roughly how many people, and the feel you\u2019re going for.'
}, {
  number: '02',
  name: 'We Build a Custom Menu & Quote',
  description: 'We put together drinks and food that fit your group and your budget, then send it over for review.'
}, {
  number: '03',
  name: 'We Handle Setup and Service',
  description: 'Our team arrives early, sets up, serves your guests, and cleans up. You get to be a host.'
}];
export function Catering() {
  return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-231" data-mp="1zuqf770ic62l">
      <SEO title="Coffee Catering in Livermore & the Tri-Valley | Grounded Mornings" description="Grounded Mornings brings craft coffee, espresso bar service, pastries, and made-to-order food to corporate meetings, weddings, and community gatherings across Livermore and the Tri-Valley." canonical="https://www.groundedlivermore.com/catering" data-id="element-232" data-mp="24tup5zv9a7tz" />
      <HamburgerMenu data-id="element-233" data-mp="jf70k9vnmdfo" />

      <main data-id="element-234" data-mp="jfyjol82eip2">
        {/* Header */}
        <header className="pt-32 pb-20 px-6 md:px-12 lg:px-24 max-w-5xl mx-auto" data-id="element-235" data-mp="jgrr7oeulc89">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-236" data-mp="ofkxowvpw2yx">
            <div className="flex items-center gap-3 mb-6" data-id="element-237" data-mp="745xhx8p3sw9">
              <div className="h-px w-12 bg-[#D97E3A]" data-id="element-238" data-mp="74penbqiud98" />
              <span className="text-[#D97E3A] font-inter font-medium tracking-widest text-xs uppercase" data-id="element-239" data-mp="25l7srr2krm2">
                Grounded Mornings · Catering
              </span>
            </div>

            <h1 className="font-bebas text-5xl md:text-7xl lg:text-8xl leading-[0.9] mb-8" data-id="element-240" data-mp="uu08e99gf7dv">
              Coffee Catering in Livermore &amp; the Tri-Valley
            </h1>

            <p className="font-inter text-lg text-white/70 leading-relaxed max-w-3xl border-l-2 border-[#D97E3A] pl-6" data-id="element-241" data-mp="pwqkwz5ml5so">
              Bring the Grounded experience to your venue. Our catering service,
              Grounded Mornings, delivers the same elevated hospitality that
              makes our shop special — perfect for corporate meetings, weddings,
              and community gatherings.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4" data-id="element-242" data-mp="131bee04yklub">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors group" data-id="element-243" data-mp="132mhryuhrrkw">
                REQUEST CATERING
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} aria-hidden="true" data-id="element-244" data-mp="1h2e2eifik67v" />
              </a>
              <a href="tel:+19252920433" className="inline-flex items-center justify-center gap-3 border border-white text-white px-8 py-4 font-bebas text-xl tracking-wide hover:border-[#D97E3A] hover:text-[#D97E3A] transition-colors" data-id="element-245" data-mp="1lxtfh85p5y38">
                <Phone size={20} aria-hidden="true" data-id="element-246" data-mp="3sq3wbs2303sc" />
                (925) 292-0433
              </a>
            </div>
          </motion.div>
        </header>

        {/* What We Bring */}
        <section aria-labelledby="what-we-bring-heading" className="px-6 md:px-12 lg:px-24 py-20 bg-[#111] border-y border-white/10" data-id="element-247" data-mp="3p8fz1hyacfcf">
          <div className="max-w-5xl mx-auto" data-id="element-248" data-mp="3k8tjh1uzin0g">
            <motion.h2 id="what-we-bring-heading" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="font-bebas text-5xl md:text-6xl leading-none mb-12" data-id="element-249" data-mp="31hoqhn9p5sy">
              What We <span className="text-[#D97E3A]" data-id="element-250" data-mp="31komqecqw2b5">Bring</span>
            </motion.h2>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10" data-id="element-251" data-mp="3g92wzachqt1q">
              {whatWeBring.map((item, i) => <motion.div key={item.name} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className="flex gap-4" data-id="element-252" data-mp="3bgjblr7katmd" data-mpi={i}>
                  <item.icon className="text-[#D97E3A] shrink-0 mt-1" size={26} strokeWidth={1.5} aria-hidden="true" data-id="element-253" data-mp="3ci4njzi7rlgb" />
                  <div data-id="element-254" data-mp="3h8tdhae8wadc">
                    <dt className="font-bebas text-2xl tracking-wide mb-2" data-id="element-255" data-mp="3haecbc7lefh0">
                      {item.name}
                    </dt>
                    <dd className="font-inter text-base text-white/60 leading-relaxed" data-id="element-256" data-mp="3ma0lnakavbkj">
                      {item.description}
                    </dd>
                  </div>
                </motion.div>)}
            </dl>
          </div>
        </section>

        {/* Perfect For */}
        <section aria-labelledby="perfect-for-heading" className="px-6 md:px-12 lg:px-24 py-20" data-id="element-257" data-mp="2yhafxj2zpoip">
          <div className="max-w-5xl mx-auto" data-id="element-258" data-mp="33izrczu4k47b">
            <motion.h2 id="perfect-for-heading" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="font-bebas text-5xl md:text-6xl leading-none mb-12" data-id="element-259" data-mp="dsw4h4rtl8gl">
              Perfect <span className="text-[#D97E3A]" data-id="element-260" data-mp="1yci0dmoqnglj">For</span>
            </motion.h2>

            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-261" data-mp="1ti429cioyvd6">
              {perfectFor.map((item, i) => <motion.div key={item.name} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.06
            }} className="border border-white/10 p-8 bg-[#0f0f0f]" data-id="element-262" data-mp="1ok2iepdfys0n" data-mpi={i}>
                  <item.icon className="text-[#D97E3A] mb-4" size={30} strokeWidth={1.5} aria-hidden="true" data-id="element-263" data-mp="15msvgy8wqwen" />
                  <dt className="font-bebas text-2xl tracking-wide mb-2" data-id="element-264" data-mp="34ybv2m8bmsbn">
                    {item.name}
                  </dt>
                  <dd className="font-inter text-base text-white/60 leading-relaxed" data-id="element-265" data-mp="2zxxxjy4cj0rv">
                    {item.description}
                  </dd>
                </motion.div>)}
            </dl>
          </div>
        </section>

        {/* How It Works */}
        <section aria-labelledby="how-it-works-heading" className="px-6 md:px-12 lg:px-24 py-20 bg-[#111] border-y border-white/10" data-id="element-266" data-mp="2011sj76nnlzb">
          <div className="max-w-5xl mx-auto" data-id="element-267" data-mp="241lmy2zugbhr">
            <motion.h2 id="how-it-works-heading" initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="font-bebas text-5xl md:text-6xl leading-none mb-12" data-id="element-268" data-mp="1q3dry0mdbk98">
              How It <span className="text-[#D97E3A]" data-id="element-269" data-mp="1l6wxkooks9np">Works</span>
            </motion.h2>

            <ol className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-270" data-mp="3bi1hbstsniol">
              {steps.map((step, i) => <motion.li key={step.number} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} data-id="element-271" data-mp="3fibnp6li0b3z" data-mpi={i}>
                  <span className="block font-bebas text-6xl text-[#D97E3A]/30 leading-none mb-3" aria-hidden="true" data-id="element-272" data-mp="1dn5pls5xpknk">
                    {step.number}
                  </span>
                  <h3 className="font-bebas text-2xl tracking-wide mb-3" data-id="element-273" data-mp="1nho0pidokihe">
                    {step.name}
                  </h3>
                  <p className="font-inter text-base text-white/60 leading-relaxed" data-id="element-274" data-mp="1wk6xbkqvhyv7">
                    {step.description}
                  </p>
                </motion.li>)}
            </ol>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="px-6 md:px-12 lg:px-24 py-24" data-id="element-275" data-mp="26xnfruf0bcf">
          <div className="max-w-3xl mx-auto text-center" data-id="element-276" data-mp="3tcpd6q4a5lyp">
            <h2 className="font-bebas text-5xl md:text-6xl mb-6" data-id="element-277" data-mp="3tea84guib6x3">
              Let's Talk About Your Event
            </h2>
            <p className="font-inter text-lg text-white/70 mb-10 leading-relaxed" data-id="element-278" data-mp="1gyxlrxt6wlki">
              Tell us the date and the details, and we'll put together a menu
              and quote that fits.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-279" data-mp="17yixcpkxiqcs">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors group" data-id="element-280" data-mp="180lxw3jpk1vr">
                REQUEST CATERING
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} aria-hidden="true" data-id="element-281" data-mp="1vudfu2trshgw" />
              </a>
              <a href="tel:+19252920433" className="inline-flex items-center justify-center gap-3 border border-white text-white px-8 py-4 font-bebas text-xl tracking-wide hover:border-[#D97E3A] hover:text-[#D97E3A] transition-colors" data-id="element-282" data-mp="2oc94643fnsq4">
                <Phone size={20} aria-hidden="true" data-id="element-283" data-mp="2qy8q2n841ptz" />
                CALL (925) 292-0433
              </a>
            </div>
          </div>
        </section>

        <NewsletterSignup data-id="element-284" data-mp="30vd39xjzaayn" />

        <footer className="border-t border-white/10 py-12 text-center" data-id="element-285" data-mp="30u1zvyug3582">
          <p className="font-bebas text-2xl text-white/50" data-id="element-286" data-mp="34wgkandrid1g">
            © {new Date().getFullYear()} GROUNDED LIVERMORE
          </p>
        </footer>
      </main>
    </div>;
}